import { createFileRoute, Link } from "@tanstack/react-router";
import { useLang } from "@/hooks/use-language";
import { LegalShell } from "@/components/legal/LegalShell";

export const Route = createFileRoute("/terms")({
  component: TermsPage,
  head: () => ({
    meta: [
      { property: "og:url", content: "https://jozori.com/terms" },
      { title: "Terms of Service — Jozori | الشروط والأحكام — جذوري" },
      {
        name: "description",
        content:
          "Terms of use for the Jozori bilingual learning platform. شروط استخدام منصة جذوري التعليمية.",
      },
    ],
    links: [{ rel: "canonical", href: "https://jozori.com/terms" }],
  }),
});

function TermsPage() {
  const { lang } = useLang();
  const isAr = lang === "ar";

  return (
    <LegalShell
      titleEn="Terms of Service"
      titleAr="الشروط والأحكام"
      updatedEn="May 19, 2026"
      updatedAr="19 مايو 2026"
    >
      {isAr ? <Ar /> : <En />}
    </LegalShell>
  );
}

function En() {
  return (
    <>
      <p>
        These Terms govern your use of the Jozori bilingual learning platform
        provided by Bilal Almadani ("Jozori", "we", "us"). By creating an
        account or continuing to use the service, you agree to these Terms.
      </p>

      <h2>1. Who can use Jozori</h2>
      <p>
        Jozori is intended for use by parents and legal guardians aged 18 or older,
        who may create child profiles for children under their care. By creating an
        account you confirm you are at least 18 and have authority to consent on
        behalf of the children whose profiles you create.
      </p>

      <h2>2. Acceptable use</h2>
      <ul>
        <li>Use Jozori only for personal, non-commercial family education.</li>
        <li>Do not share your account, parent PIN, or premium content.</li>
        <li>Do not attempt to download, copy, scrape, republish, or reverse engineer Jozori content or software.</li>
        <li>Do not use Jozori to upload unlawful, infringing, harmful, or abusive content, or to interfere with the security of the service.</li>
      </ul>

      <h2>3. Intellectual property</h2>
      <p>
        All stories, illustrations, audio, videos, software, branding, and
        activities are owned by Jozori or its licensors and are provided for
        personal family use only. We grant you a limited, non-exclusive,
        non-transferable, revocable licence to use the service within your active
        plan.
      </p>

      <h2>4. Subscriptions, payments and Merchant of Record</h2>
      <p>
        Our order process is conducted by our online reseller{" "}
        <strong>Paddle.com</strong>. Paddle.com is the Merchant of Record for all
        our orders. Paddle provides all customer-service inquiries related to
        billing and handles returns and refunds. Payment, billing, sales tax,
        cancellation, and refund mechanics are governed by{" "}
        <a href="https://www.paddle.com/legal/checkout-buyer-terms" target="_blank" rel="noreferrer">
          Paddle's Checkout Buyer Terms
        </a>.
      </p>
      <p>
        Paid plans renew automatically until cancelled. You can cancel any time
        from <em>Settings → Plan</em>; access continues until the end of the
        current billing period. See our <Link to="/refunds">Refund Policy</Link>{" "}
        for details on the 30-day money-back guarantee.
      </p>

      <h2>5. Account security</h2>
      <p>
        You are responsible for keeping your credentials and parent PIN
        confidential and for activity on your account. Notify us immediately at{" "}
        <a href="mailto:security@jozori.com">security@jozori.com</a> if you
        suspect unauthorised access.
      </p>

      <h2>6. Service level and warranties</h2>
      <p>
        We work hard to keep Jozori running smoothly, but we don't guarantee
        uninterrupted or error-free performance. To the fullest extent permitted
        by law, the service is provided "as is" without implied warranties of
        merchantability or fitness for a particular purpose.
      </p>

      <h2>7. Limitation of liability</h2>
      <p>
        To the maximum extent permitted by law, our aggregate liability under
        these Terms is limited to the fees you paid us (or Paddle on our behalf)
        in the 12 months preceding the claim. We are not liable for indirect,
        consequential, or special damages including loss of profits, data, or
        goodwill. Nothing in these Terms limits liability for fraud, death, or
        personal injury where such limitation is not permitted.
      </p>

      <h2>8. Suspension and termination</h2>
      <p>
        We may suspend or terminate access for material breach of these Terms,
        non-payment, security or fraud risk, or repeated policy violations. On
        termination your data is handled in accordance with our{" "}
        <Link to="/privacy">Privacy Policy</Link>.
      </p>

      <h2>9. Children's safety</h2>
      <p>
        Jozori is designed to keep children safe: no ads, no chat, no third-party
        tracking, parent-gated settings, and human-reviewed content. See our{" "}
        <Link to="/safety">Child Safety page</Link> for full detail.
      </p>

      <h2>10. Changes</h2>
      <p>
        We may update these Terms with reasonable notice to your account email.
        Continued use after the effective date constitutes acceptance.
      </p>

      <h2>11. Governing law</h2>
      <p>
        These Terms are governed by the laws of England and Wales. Disputes are
        subject to the exclusive jurisdiction of the courts of England and Wales,
        without limiting any non-waivable consumer rights in your country of
        residence.
      </p>

      <h2>12. Contact</h2>
      <p>
        Bilal Almadani — <a href="mailto:hello@jozori.com">hello@jozori.com</a>.
      </p>
    </>
  );
}

function Ar() {
  return (
    <>
      <p>
        تنظّم هذه الشروط استخدامك لمنصة جذوري العائلية ثنائية اللغة المقدّمة من
        بلال المداني ("جذوري"، "نحن"). بإنشائك حسابًا أو متابعتك
        استخدام الخدمة، فإنك توافق على هذه الشروط.
      </p>

      <h2>١. من يحق له استخدام جذوري</h2>
      <p>
        جذوري مخصّصة للاستخدام من قِبل الآباء والأولياء القانونيين البالغين من
        العمر 18 سنة فأكثر، الذين يمكنهم إنشاء ملفات للأطفال الذين تحت رعايتهم.
        بإنشائك حسابًا، فإنك تؤكّد أنك بالغ السن القانوني وأنك مخوّل بالموافقة
        نيابة عن الأطفال الذين تنشئ ملفاتهم.
      </p>

      <h2>٢. الاستخدام المقبول</h2>
      <ul>
        <li>استخدم جذوري للأغراض العائلية الشخصية وغير التجارية فقط.</li>
        <li>لا تشارك حسابك، أو رمز PIN الخاص بالوالد، أو المحتوى المدفوع مع أحد.</li>
        <li>لا تحاول تنزيل المحتوى أو نسخه أو سحبه أو إعادة نشره أو إجراء هندسة عكسية للبرنامج.</li>
        <li>لا تستخدم جذوري لرفع محتوى غير قانوني أو مسيء أو ضار، ولا للإخلال بأمان الخدمة.</li>
      </ul>

      <h2>٣. الملكية الفكرية</h2>
      <p>
        جميع القصص والرسوم والصوتيات والفيديوهات والبرامج والعلامات التجارية
        والأنشطة هي ملك لجذوري أو للمرخّصين لها، ومقدَّمة للاستخدام العائلي
        الشخصي فقط. نمنحك ترخيصًا محدودًا، غير حصري، وغير قابل للتحويل أو النقل،
        لاستخدام الخدمة ضمن باقتك النشطة.
      </p>

      <h2>٤. الاشتراكات والمدفوعات وتاجر التسجيل</h2>
      <p>
        تتمّ عملية الطلب عبر موزّعنا الإلكتروني{" "}
        <strong>Paddle.com</strong>، وهو تاجر التسجيل (Merchant of Record) لجميع
        طلباتنا. تتولّى Paddle الاستفسارات المتعلّقة بالفوترة وعمليات الإرجاع
        والاسترداد. تخضع مسائل الدفع والفوترة والضرائب والإلغاء والاسترداد إلى{" "}
        <a href="https://www.paddle.com/legal/checkout-buyer-terms" target="_blank" rel="noreferrer">
          شروط Paddle للمشتري
        </a>.
      </p>
      <p>
        تتجدّد الباقات المدفوعة تلقائيًا حتى يتم إلغاؤها. يمكنك الإلغاء في أي
        وقت من <em>الإعدادات ← الاشتراك</em>، ويستمرّ وصولك حتى نهاية فترة
        الفوترة الحالية. راجع{" "}
        <Link to="/refunds">سياسة الاسترداد</Link> للاطلاع على ضمان استرداد
        المبلغ خلال 30 يومًا.
      </p>

      <h2>٥. أمان الحساب</h2>
      <p>
        أنت مسؤول عن الحفاظ على سرية بيانات الدخول ورمز PIN الخاص بالوالد، وعن
        النشاط الذي يتم عبر حسابك. تواصل معنا فورًا على{" "}
        <a href="mailto:security@jozori.com">security@jozori.com</a> إذا اشتبهت
        بأي وصول غير مصرّح به.
      </p>

      <h2>٦. مستوى الخدمة والضمانات</h2>
      <p>
        نعمل بجدّ لإبقاء جذوري تعمل بسلاسة، لكنّنا لا نضمن توفّرًا متواصلًا أو
        أداءً خاليًا من الأخطاء. إلى أقصى حدّ يسمح به القانون، تُقدَّم الخدمة
        "كما هي" دون أي ضمانات ضمنية للتسويق أو الملاءمة لغرض معيّن.
      </p>

      <h2>٧. حدود المسؤولية</h2>
      <p>
        إلى أقصى حدّ يسمح به القانون، تقتصر مسؤوليتنا الإجمالية بموجب هذه الشروط
        على الرسوم التي دفعتها لنا (أو لـ Paddle نيابةً عنا) خلال الاثني عشر
        شهرًا التي تسبق المطالبة. لسنا مسؤولين عن الأضرار غير المباشرة أو
        التبعية أو الخاصة، بما فيها فقدان الأرباح أو البيانات أو السمعة. لا
        يحدّ شيء في هذه الشروط من المسؤولية عن الاحتيال أو الوفاة أو الإصابة
        الشخصية حيث لا يكون هذا التحديد مسموحًا به.
      </p>

      <h2>٨. التعليق والإنهاء</h2>
      <p>
        يحقّ لنا تعليق أو إنهاء الوصول في حال الإخلال الجوهري بهذه الشروط، أو
        عدم الدفع، أو مخاطر الأمان والاحتيال، أو تكرار انتهاك السياسات. عند
        الإنهاء، تتم معالجة بياناتك وفق{" "}
        <Link to="/privacy">سياسة الخصوصية</Link>.
      </p>

      <h2>٩. سلامة الأطفال</h2>
      <p>
        صُمّمت جذوري لتحافظ على سلامة الأطفال: بدون إعلانات، بدون محادثات، بدون
        تتبّع خارجي، مع إعدادات محميّة بقفل الوالد ومحتوى يراجعه فريق بشري.
        راجع <Link to="/safety">صفحة سلامة الأطفال</Link> للتفاصيل الكاملة.
      </p>

      <h2>١٠. التعديلات</h2>
      <p>
        قد نقوم بتحديث هذه الشروط مع إشعار معقول إلى البريد الإلكتروني المسجَّل
        في حسابك. يُعدّ استمرار استخدامك بعد تاريخ السريان موافقةً منك.
      </p>

      <h2>١١. القانون الحاكم</h2>
      <p>
        تخضع هذه الشروط لقوانين إنجلترا وويلز، وتختصّ بنظر النزاعات محاكم
        إنجلترا وويلز حصريًا، دون المساس بأي حقوق غير قابلة للتنازل تكفلها
        قوانين حماية المستهلك في بلد إقامتك.
      </p>

      <h2>١٢. التواصل</h2>
      <p>
        Bilal Almadani — <a href="mailto:hello@jozori.com">hello@jozori.com</a>.
      </p>
    </>
  );
}
